import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import loginImage from '../assets/images/login-image.png';

const OTPVerification: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const email = location.state?.email || ''; // Get email passed from Signup page
  const [otp, setOtp] = useState<string[]>(['', '', '', '', '', '']); // For 6 OTP digits
  const [message, setMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null); // Message state for success/error

  // Handle input change for each OTP box
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    const newOtp = [...otp];

    if (/^\d*$/.test(value)) {
      newOtp[index] = value;
      setOtp(newOtp);

      // Auto-focus to next input field if value is entered
      if (value && index < otp.length - 1) {
        const nextInput = document.getElementById(`otp-${index + 1}`);
        nextInput?.focus();
      }
    }
  };

  // Handle OTP submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpValue = otp.join(''); // Join OTP array to create the full OTP string
    const email = location.state?.email; // Get the email passed from the previous page
  
    // Validation: Check if the OTP and email are valid
    if (otpValue.length !== 6 || !/^\d+$/.test(otpValue)) {
      setMessage({ text: 'Please enter a valid 6-digit OTP.', type: 'error' });
      return;
    }
    if (!email) {
      setMessage({ text: 'Email is missing! Please provide a valid email.', type: 'error' });
      return;
    }
  
    try {
      // Make API call with both email and OTP in the query parameters
      const response = await fetch(
        `https://pamsapi.vofoxsolutions.com/api/Account/validate-otp?email=${encodeURIComponent(email)}&otp=${otpValue}`,
        {
          method: 'GET', // Use the correct method as per the API requirement
        }
      );
  
      if (response.ok) {
        setMessage({ text: 'OTP validated successfully!', type: 'success' });
        // Redirect to the registration page or next step
        navigate('/home');
      } else {
        setMessage({ text: 'Invalid OTP! Please try again.', type: 'error' });
      }
    } catch (error) {
      console.error('Error during OTP validation:', error);
      setMessage({ text: 'Something went wrong during OTP verification!', type: 'error' });
    }
  };

  // Handle Resend OTP
  const handleResendOTP = async () => {
    const email = location.state?.email; // Access email directly from location state
    
    if (!email) {
      setMessage({ text: 'Please provide a valid email!', type: 'error' });
      return;
    }
  
    try {
      // Trigger the API to generate a new OTP for the given email
      const response = await fetch('https://pamsapi.vofoxsolutions.com/api/Account/generate-otp', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json', // Set the correct Content-Type
        },
        body: JSON.stringify({ email }) // Send the email as a JSON body
      });
  
      if (response.ok) {
        const responseData = await response.json();
        setMessage({ text: 'OTP resent successfully!', type: 'success' });
  
        // Reset OTP input fields to empty state
        setOtp(['', '', '', '', '', '']); // Reset OTP fields
      } else {
        const errorData = await response.json();
        console.error('Error resending OTP:', errorData);
        setMessage({ text: 'Failed to resend OTP!', type: 'error' });
      }
    } catch (error) {
      console.error('Error during OTP resending:', error);
      setMessage({ text: 'Something went wrong while resending OTP!', type: 'error' });
    }
  };
  

  return (
    <div className="content-wrapper">
      <main>
        <div className="mx-auto max-w-screen-2xl p-4 md:p-6 2xl:p-10">
          <div className="flex flex-wrap items-center">
            {/* Left Image Section */}
            <div className="hidden w-full h-full lg:block lg:w-1/2">
              <div className="px-26 py-17.5 text-center">
                <span className="mt-15 inline-block">
                  <img
                    className="h-full w-full object-cover"
                    src={loginImage}
                    alt="illustration"
                  />
                </span>
              </div>
            </div>

            {/* Right Form Section */}
            <div className="w-full lg:w-1/2">
              <div className="w-full lg:w-3/4 p-4 sm:p-12.5 xl:p-17.5 m-auto">
                <a className="mb-8 inline-block" href="/">
                  <img src="/assets/images/logo-1.svg" alt="Logo" />
                </a>
                <h2 className="mb-2 text-3xl font-semibold text-dark">Verify Your Account</h2>
                <p className="mb-9 block font-normal text-lg text-jetgrey">
                  We sent a code to <strong>{email}</strong>
                </p>

                {/* OTP Input Form */}
                <form onSubmit={handleSubmit}>
                  <div className="mb-6">
                    {/* Grid layout for 6 OTP input fields */}
                    <div className="grid grid-cols-6 gap-4 w-full">
                      {otp.map((digit, index) => (
                        <input
                          key={index}
                          id={`otp-${index}`}
                          type="text"
                          value={digit}
                          onChange={(e) => handleChange(e, index)}
                          maxLength={1}
                          className="text-center border border-whitesmoke rounded-lg bg-ghostwhite py-4 outline-none focus:border-blue focus-visible:shadow-none w-full"
                        />
                      ))}
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="mt-6">
                    <input
                      type="submit"
                      value="Verify OTP"
                      className="w-full cursor-pointer bg-blue-600 shadow-lg shadow-blue-300/50 rounded-lg border border-primary p-4 font-medium text-white transition hover:bg-blue-500"
                    />
                  </div>
                </form>

                {/* Display Success or Error Message */}
                {message && (
                  <div
                    className={`mt-4 p-4 rounded-lg text-white ${
                      message.type === 'success' ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  >
                    {message.text}
                  </div>
                )}

                {/* Resend OTP */}
                <div className="flex justify-between items-center mt-6">
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    className="text-blue font-medium hover:underline"
                  >
                    Resend OTP
                  </button>
                  <a href="/signin" className="text-blue font-medium hover:underline">
                    Back to Sign In
                  </a>
                </div>

                {/* Troubleshooting Tips */}
                <div className="mt-8">
                  <p className="font-semibold">Troubleshooting Tips:</p>
                  <ul className="list-outside mt-4 space-y-2">
                    <li className="flex gap-3 text-sm text-jetgrey">
                      <span className="flex rounded-full border border-blue text-blue h-5 w-5 items-center justify-center text-xs font-semibold">
                        1
                      </span>
                      Confirm you entered the correct email address.
                    </li>
                    <li className="flex gap-3 text-sm text-jetgrey">
                      <span className="flex rounded-full border border-blue text-blue h-5 w-5 items-center justify-center text-xs font-semibold">
                        2
                      </span>
                      Check your spam or junk folder.
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OTPVerification;
